/* eslint-disable no-return-assign */
/* eslint-disable react/no-array-index-key */
import { Document, Font, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { getPdfCss } from './course-details-pdf-css';
import {
  generateImportantDatesRow,
  generateImportantDatesRowUSPathway,
} from './section/course-details-important-dates-row';
import { generateFeesAndPricingRow } from './section/course-details-feesAndPricing-row';
import { generateModulesOrClassesRow } from './section/course-details-modules-row';
import { saveAs } from 'file-saver';
import { type CourseDetailsPdfData } from '../course-details/course-details';

const hanSansSCPath = `/next-assets/fonts/HanSansSC/`;

Font.register({
  family: `NeoSansPro`,
  src: `/next-assets/fonts/NeoSans/b2931674-c992-42a0-aa22-e9cb15d3383d.ttf`,
});

Font.register({
  family: `Lato`,
  src: `/next-assets/fonts/Lato/Lato-Regular.woff`,
});

Font.register({
  family: `LatoBold`,
  src: `/next-assets/fonts/Lato/Lato-Bold.woff`,
});

Font.register({
  family: `HanSansSC`,
  // https://www.npmjs.com/package/source-han-sans-sc-ttf
  fonts: [
    { src: `${hanSansSCPath}SourceHanSansSC-Regular.ttf`, fontWeight: 400 },
    { src: `${hanSansSCPath}SourceHanSansSC-Bold.ttf`, fontWeight: 700 },
    { src: `${hanSansSCPath}SourceHanSansSC-Heavy.ttf`, fontWeight: 900 },
  ],
});

let orientation: any = `portrait`;

// React PDF doesn`t support white-space css attribute, so here`s a horrible
// hack to get words (particularly URLs) to fit into a 5 col table layout.
const hyphenationCallback = (word) => {
  const breakIdentifiers = [`https`, `http`];
  const maxSizePortrait = 20;
  const maxSizeLandscape = 27;

  let result;

  breakIdentifiers.find((elem) => {
    // https://itnext.io/javascript-split-a-string-into-equal-lengths-848eb811f383
    if (word.includes(elem)) {
      const max_size = orientation === `landscape` ? maxSizeLandscape : maxSizePortrait;
      const yardstick = new RegExp(`.{${max_size}}`, `g`);
      const pieces = word.match(yardstick);
      const accumulated = pieces.length * max_size;
      const modulo = word.length % accumulated;
      if (modulo) pieces.push(word.slice(accumulated));

      // return truthy result to stop the loop
      return (result = pieces);
    }

    return (result = [word]);
  });

  // Chinese characters can't wrap lines as automatically as English characters
  // Especially if EN/CH text has ( ) in it, like:
  // 国际大学预科课程人文学科与社会科学方向（International Foundation in Humanities and Social Sciences）
  // BA (Hons) Accounting and Business Studies with International Year Two
  // https://github.com/diegomura/react-pdf/issues/692#issuecomment-582718925
  result = word.length === 1 ? [word] : Array.from(word).map((char) => char);

  return result;
};
Font.registerHyphenationCallback(hyphenationCallback);

// Create Document Component
export const CourseDetailsPdf: React.FC<{
  data: CourseDetailsPdfData;
  currentLocale: string;
  translations;
}> = ({ data, currentLocale, translations }) => {
  // Create styles
  const styles = StyleSheet.create(getPdfCss(currentLocale));
  const date = new Date();
  const progressionLanguageReq = data?.progression?.languageRequirements ? (
    <View style={styles.tableRow}>
      <Text style={[styles.tableCell]}>
        <Text style={styles.paragraph}>
          English level: {data?.progression?.languageRequirements}
        </Text>
      </Text>
    </View>
  ) : null;
  return (
    <Document>
      <Page size='A4' orientation={orientation} style={styles.page}>
        <View style={[styles.tableRow, styles.header]}>
          <Text style={styles.headerText}>Course Details</Text>
        </View>
        <View style={[styles.tableRow, styles.courseSubHead]}>
          <Text style={styles.courseHeader}>{data.programTitle}</Text>
        </View>
        <View style={[styles.tableRow, styles.courseTermHeader]}>
          <Text style={styles.coursesubHeader}>
            {data.brandName} | {data.courseDuration}
          </Text>
        </View>
        <View style={{ flexGrow: 1 }}>
          <View>
            <View style={[styles.tableRow, styles.rowHeading, styles.courseDetailsHeader]}>
              <View style={styles.courseDetailHeadText}>
                <Text>Entry Requirements</Text>
              </View>
            </View>
            <View style={styles.courseDetail}>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell]}>
                  <Text style={styles.paragraphHeading}>Academic Requirement</Text>
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.paragraph, styles.marginbottom20]}>
                  {data.academic}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell]}>
                  <Text style={styles.paragraphHeading}>English Language Requirements</Text>
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.paragraph, styles.marginbottom20]}>
                  {data.language}
                </Text>
              </View>

              {data?.hasApplicationFees && data?.applicationFee?.amount != 0 ? (
                <View>
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableCell]}>
                      <Text style={styles.paragraphHeading}>Application Fee</Text>
                    </Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.paragraph,
                        styles.marginbottom20,
                        styles.marginright50,
                      ]}
                    >
                      Application Fee
                    </Text>
                    <Text style={[styles.tableCell, styles.paragraph, styles.marginbottom20]}>
                      ${data.applicationFee.amount}
                    </Text>
                  </View>
                </View>
              ) : null}

              <View style={styles.tableRow}>
                <Text style={[styles.tableCell]}>
                  <Text style={styles.paragraphHeading}>Note</Text>
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.paragraph]}>{data.endNote}</Text>
              </View>
            </View>
          </View>
          {(data?.terms?.length > 0 || data?.courseInfos?.length > 0) && (
            <View>
              <View style={[styles.tableRow, styles.rowHeading, styles.courseDetailsHeader]}>
                <View style={styles.courseDetailHeadText}>
                  <Text>Important Dates</Text>
                </View>
              </View>
              <View style={styles.courseDetail}>
                {!data.canShowSeasons &&
                  generateImportantDatesRow(data.terms, data.programLength, currentLocale, styles)}
                {data.canShowSeasons &&
                  generateImportantDatesRowUSPathway(
                    data.courseInfos,
                    data?.brandOptions?.termName,
                    translations,
                    styles,
                    data.isUSPathway,
                  )}
              </View>
            </View>
          )}
          {data?.feesAndPricingData.feesList?.length > 0 ? (
            <View>
              <View style={[styles.tableRow, styles.rowHeading, styles.courseDetailsHeader]}>
                <View style={styles.courseDetailHeadText}>
                  <Text>Fees and Pricing</Text>
                </View>
              </View>
              <View style={styles.courseDetail}>
                {generateFeesAndPricingRow(
                  data.feesAndPricingData.feesList,
                  data.feesAndPricingData.feesTotal,
                  data.feesAndPricingData.feesNote,
                  currentLocale,
                  styles,
                )}
              </View>
            </View>
          ) : null}
          {data?.modules?.length > 0 ? (
            <View>
              {generateModulesOrClassesRow(
                data.brandOptions,
                data.modules,
                data.brandCountry,
                currentLocale,
                styles,
                data.degreeCredits,
                data.classNotes,
              )}
            </View>
          ) : null}
          {data?.progression?.progressionRequirements !== undefined ||
          data?.progression?.languageRequirements !== undefined ? (
            <View>
              <View style={[styles.tableRow, styles.rowHeading, styles.courseDetailsHeader]}>
                <View style={[styles.courseDetailHeadText]}>
                  <Text>Progression</Text>
                </View>
              </View>
              <View style={styles.courseDetail}>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell]}>
                    <Text style={styles.paragraph}>
                      {data?.progression?.progressionRequirements}
                    </Text>
                  </Text>
                </View>
                {progressionLanguageReq}
              </View>
            </View>
          ) : null}
        </View>
        <View style={[styles.pdfFooterRow]}>
          <Text style={[styles.footerdate]}>
            Produced on{' '}
            {new Date(date).toLocaleDateString(currentLocale, {
              dateStyle: `long`,
            })}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export async function createAndSavePdf(pdfContents: React.ReactElement, fileName: string) {
  const blob = await pdf(pdfContents).toBlob();
  saveAs(blob, fileName);
}
